import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import { push } from 'connected-react-router';

import styled from 'styled-components';

// Types
import { ArtistType } from '@common/types/artist/artist-type';
import SimpleArtist from '@common/types/artist/simple-artist';
import { NewInstance } from '@common/types/data/new-instance';

// Store
import AppState from '@webapp/store/state';
import { ResetError } from '@webapp/store/ui/action-creators';
import { CREATE_ARTIST_ERROR } from '@webapp/store/artist/actions';
import {
  UpdateTracPageDataField,
  UpdateTracPageField,
} from '@webapp/store/tracpage/action-creators';

// Services
import TracPage from '@services/tracpage/types/trac-page';
import { TracThemeType } from '@services/tracpage/types/trac-theme-type';
import { Subscription } from '@services/subscription/types/subscription';
import { TracPageSocialLink } from '@services/tracpage/types/track-page-social-link';

// Components
import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import SquareAvatar from '@components/basics/square-avatar';
import CustomDomain from '@components/artist/custom-domain';
import MotionOpacity from '@components/motion/motion-opacity';
import TextareaWithCount from '@components/form/textareawithcount';

// Controllers
import DarkLightCtrl from '@webapp/controllers/dark-light-dropdown';
import SocialNetworkListCtrl from '@webapp/controllers/form/social-network-list';
import CustomUrlInputCtrl from '@webapp/controllers/artist/artist-profile/custom-url-input';
import EditArtistProfileMenu from '@webapp/controllers/menus/edit-artist-profile-menu';
import { rudderanalytics } from '@webapp/utils/rudderstack/init';

interface ArtistSettingsGeneralProps
  extends Partial<NewInstance<SimpleArtist>> {
  className?: string;
  disableAvatar?: boolean;
  disableStreaming?: boolean;
  noHint?: boolean;
  hasTitle?: React.ReactNode;
  showErrors: boolean;
  artistCreateError?: string;
  hideStreamingPlatforms?: boolean;
  currentSubscriptions?: Subscription[];
  social: TracPageSocialLink[];
  bio: string;
  artistId: string;
  theme: TracThemeType;
  updateTheme: (value: TracThemeType) => UpdateTracPageField<TracPage, 'theme'>;
  onChange: (data: NewInstance<SimpleArtist>) => void;
  resetArtistCreateError: () => ResetError;
  updateBio: (value: string) => UpdateTracPageDataField<TracPage, 'bio'>;
  updateNetworks: (
    value: TracPageSocialLink[]
  ) => UpdateTracPageDataField<TracPage, 'social'>;
  onSaveChanges: (isNameUpdate: boolean) => void;
}

const mapStateToProps = (state: AppState) => ({
  artistCreateError: state.ui.errors[CREATE_ARTIST_ERROR],
  social: state.tracpage.artistPage.data.social,
  bio: state.tracpage.artistPage.data.bio || '',
  theme: state.tracpage.artistPage.theme,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  resetArtistCreateError: () => dispatch(new ResetError(CREATE_ARTIST_ERROR)),
  goToSubscription: () => dispatch(push(`/user/subscriptions`)),
  updateNetworks: (value: TracPageSocialLink[]) =>
    dispatch(
      new UpdateTracPageDataField<TracPage, 'social'>(
        'tracpage',
        'social',
        value
      )
    ),
  updateBio: (value: string) =>
    dispatch(
      new UpdateTracPageDataField<TracPage, 'bio'>('tracpage', 'bio', value)
    ),
  updateTheme: (value: TracThemeType) =>
    dispatch(
      new UpdateTracPageField<TracPage, 'theme'>('tracpage', 'theme', value)
    ),
});

export const ArtistSettingsGeneral: React.FC<ArtistSettingsGeneralProps> = (
  props
) => {
  const propsName = props.name;
  const propsBio = props.bio;
  const propsAvatar = props.avatar;
  const propsType = props.type;
  const propPlatforms = props.platforms;

  const [name, setName] = React.useState(propsName || '');
  const [bio, setBio] = React.useState(propsBio || '');
  const [type, setType] = React.useState(propsType || undefined);

  React.useEffect(() => setName(propsName || ''), [propsName]);
  React.useEffect(() => setBio(propsBio || ''), [propsBio]);
  React.useEffect(() => setType(propsType), [propsType]);

  const handleChange = (newVal?: Partial<NewInstance<SimpleArtist>>) => {
    const newArtist: NewInstance<SimpleArtist> = {
      // @ts-ignore
      name,
      // @ts-ignore
      type: 'solo' as ArtistType,
      // avatar,
      ...(newVal as NewInstance<SimpleArtist>),
    };

    if (newArtist) {
      return props.onChange(newArtist);
    }
  };

  const handleBioChange = (value: string) => {
    if (!!props.artistCreateError) {
      props.resetArtistCreateError();
    }
    handleChange({
      bio: value,
    });

    setBio(value);
  };

  React.useEffect(() => {
    if (
      propsAvatar &&
      propsBio &&
      propsName &&
      !!props.social.length &&
      propPlatforms
    ) {
      rudderanalytics.track('Artist Profile Completed', {
        artist_id: props.artistId,
        artist_name: propsName,
        streaming_profiles: Object.entries(propPlatforms).map(
          ([key, value]) => {
            return {
              name: key,
              url: value,
            };
          }
        ),
        social_profiles: props.social.map((social) => {
          return {
            name: social.provider,
            url: social.url,
          };
        }),
        bio: propsBio,
        avatar: propsAvatar,
      });
    }
  }, [
    propsAvatar,
    propsBio,
    propsName,
    props.social,
    propPlatforms,
    props.artistId,
  ]);

  return (
    <SectionWrapper>
      <Box noBorderBottom>
        <AvatarContainer>
          <SquareAvatar src={propsAvatar} username={name} />
          <div>
            <Typography
              className="mb-5"
              type="big-title"
              customFont="semi-bold"
            >
              {name.length > 20 ? `${name.slice(0, 20)}...` : name}
            </Typography>
            <EditArtistProfileMenu
              avatar={propsAvatar}
              name={name}
              type={type}
              showErrors={props.showErrors}
              onChange={props.onChange}
              disableAvatar={props.disableAvatar}
              onSaveChanges={props.onSaveChanges}
            />
          </div>
        </AvatarContainer>
      </Box>
      <Box noBorderTop noBorderBottom>
        <Typography customFont="medium" type="big-title" className="mb-20">
          <Translate id="ARTIST_PROFILE.TRACPAGE.TITLE" />
        </Typography>
        <MotionOpacity>
          <DarkLightCtrl
            value={props.theme}
            className="mb-10"
            onChange={props.updateTheme}
          />
          <CustomUrlInputCtrl enableEditing={true} />
          <CustomDomain isLocked={true} />
        </MotionOpacity>
      </Box>
      <Box noBorderTop noBorderBottom>
        <Typography customFont="medium" type="big-title" className="mb-20">
          <Translate id="ARTIST_PROFILE.PERSONAL_INFO.TITLE" />
        </Typography>
        <TextareaWithCount
          maxLength={300}
          value={bio}
          label={<Translate id="ARTIST.CREATE.BIO" />}
          onChange={(value) => {
            handleBioChange(value);
            props.updateBio(value);
          }}
        />
      </Box>
      <Box noBorderTop>
        {!props.hideStreamingPlatforms && (
          <SocialNetworkListCtrl
            networks={props.social}
            onChange={props.updateNetworks}
            includeStreaming
          />
        )}
      </Box>
    </SectionWrapper>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ArtistSettingsGeneral);

const AvatarContainer = styled(Flex)`
  gap: 30px;
  align-items: center;
`;

const SectionWrapper = styled.div`
  display: grid;
  gap: 3px;
  margin-bottom: 80px;
`;
